import { useTranslations } from "next-intl";
import { IconUsers, IconInbox, IconLogin } from "@tabler/icons-react";
import { GuestDeleteImpact } from "@/types/GuestDeleteImpact";

/**
 * Whether the impact has a consequence line to show (cascaded companions, open
 * requests, checked-in guests). The trash dialogs mount the box — and the
 * spacing around it — only then; the permanent dialog always has its data note.
 */
export const impactHasLines = (impact: GuestDeleteImpact): boolean =>
  impact.cascadedCompanions > 0 || impact.pendingRequests > 0 || impact.checkedIn > 0;

/**
 * Consequence list for a guest delete: cascade-deleted companions, open
 * requests, checked-in guests, plus the permanent-data note. Shared between
 * the bulk confirmation (PBulkActionConfirmation) and the single-guest
 * delete popup (guest detail Actions) so the warning copy stays identical.
 *
 * `trash`: the delete moves the rows to the trash (W6) — the companions and
 * requests go there too, so the sentences say so instead of "removed
 * permanently". The trash dialog carries NO data note (the permanent one keeps
 * its "removed permanently" line): the owner wants that dialog as plain as the
 * old delete dialog, without spelling out that the delete is reversible
 * (22.10.). With nothing to list, the trash box is not rendered at all.
 */
export const DeleteImpactSummary: React.FC<{ impact: GuestDeleteImpact; trash?: boolean }> = ({
  impact,
  trash = false,
}) => {
  const t = useTranslations("bulkConfirm");
  if (trash && !impactHasLines(impact)) return null;
  return (
    <div
      className="w-full flex flex-col gap-2 surface-secondary rounded-md border outline-quaternary px-3 py-2.5"
      data-testid={trash ? "guest-trash-impact-summary" : undefined}
    >
      {impact.cascadedCompanions > 0 && (
        <div className="flex items-start gap-2">
          <IconUsers size={14} className="font-error shrink-0 mt-0.5" />
          <p className="body-3-medium font-error">
            {t(trash ? "impactCascadedCompanionsTrash" : "impactCascadedCompanions", {
              count: impact.cascadedCompanions,
              total: impact.totalToDelete,
            })}
          </p>
        </div>
      )}
      {impact.pendingRequests > 0 && (
        <div className="flex items-start gap-2">
          <IconInbox size={14} className="font-secondary shrink-0 mt-0.5" />
          <p className="body-3-regular font-secondary">
            {t(trash ? "impactPendingRequestsTrash" : "impactPendingRequests", { count: impact.pendingRequests })}
          </p>
        </div>
      )}
      {impact.checkedIn > 0 && (
        <div className="flex items-start gap-2">
          <IconLogin size={14} className="font-secondary shrink-0 mt-0.5" />
          <p className="body-3-regular font-secondary">
            {t("impactCheckedIn", { count: impact.checkedIn })}
          </p>
        </div>
      )}
      {!trash && <p className="body-3-regular font-tertiary">{t("impactDataNote")}</p>}
    </div>
  );
};
